import { ReactNode } from 'react';

import Link from 'next/link';

type HeaderItemSimple = {
  href: string;
  label: string;
};
type HeaderItemWithTrigger = {
  trigger: string;
  content: {
    main: {
      icon: ReactNode;
      title: string;
      description: string;
      href: string;
    };
    items: { title: string; description: string; href: string }[];
  };
};
type HeaderItem = HeaderItemSimple | HeaderItemWithTrigger;

export const siteConfig = {
  name: 'Saights',
  description: 'Automate your workflow with AI',
  url: process.env.NEXT_PUBLIC_APP_URL! || 'http://localhost:3000',
  keywords: ['SaaS', 'Template', 'Next.js', 'React', 'Tailwind CSS'],
  links: {
    email: 'support@saypien.io',
    twitter: 'https://twitter.com/magicuidesign',
    discord: 'https://discord.gg/87p2vpsat5',
    github: 'https://github.com/magicuidesign/magicui',
    instagram: 'https://instagram.com/magicuidesign/',
  },
  header: [
    {
      href: '/about',
      label: 'About',
    },
    {
      href: '/updates',
      label: 'Updates',
    },
    {
      href: '/#features',
      label: 'Features',
    },
    {
      href: '/#how-it-works',
      label: 'How It Works',
    },
    {
      href: '/#contact-section',
      label: 'Book a Demo',
    },
  ] as HeaderItem[],
  pricing: [
    {
      name: 'BASIC',
      href: '#',
      price: '$19',
      period: 'month',
      yearlyPrice: '$16',
      features: [
        '1 User',
        '5GB Storage',
        'Basic Support',
        'Limited API Access',
        'Standard Analytics',
      ],
      description: 'Perfect for individuals and small projects',
      buttonText: 'Subscribe',
      isPopular: false,
    },
    {
      name: 'PRO',
      href: '#',
      price: '$49',
      period: 'month',
      yearlyPrice: '$40',
      features: [
        '5 Users',
        '50GB Storage',
        'Priority Support',
        'Full API Access',
        'Advanced Analytics',
      ],
      description: 'Ideal for growing businesses and teams',
      buttonText: 'Subscribe',
      isPopular: true,
    },
    {
      name: 'ENTERPRISE',
      href: '#',
      price: '$99',
      period: 'month',
      yearlyPrice: '$82',
      features: [
        'Unlimited Users',
        '500GB Storage',
        '24/7 Premium Support',
        'Custom Integrations',
        'AI-Powered Insights',
      ],
      description: 'For large-scale operations and high-volume users',
      buttonText: 'Subscribe',
      isPopular: false,
    },
  ],
  faqs: [
    {
      section: 'General',
      qa: [
        {
          question: 'What is Saights and how does it work?',
          answer: (
            <span>
              Saights replaces manual survey creation &#38; data analysis within
              one platform. Create survey within minutes and instantly start
              gathering and analyzing data in real-time.
            </span>
          ),
        },
        {
          question: 'How is Saights different from other survey tools?',
          answer: (
            <span>
              Saights laverages AI technology to make market research
              accessible, even for those without prior experience. Our platform
              guides you through the entire process, from creating a survey to
              effortless data analysis, empowering you to gather valuable
              insights with ease.
            </span>
          ),
        },
        {
          question: 'What types of surveys can I create with Saights?',
          answer: (
            <span>
              You can create surveys for any purpose &#38; any topic you can
              imagine. Also, we support a wide range of languages you can create
              your surveys in. This feature will be availble soon for our
              waitlist users.
            </span>
          ),
        },
        {
          question: 'Is any technical knowledge required to use Saights?',
          answer: (
            <span>
              No technical knowledge is required to use Saights. Our platform is
              designed to be user-friendly and intuitive, making it easy for
              anyone to create and analyze surveys. If you have any questions,
              our AI Assistant and our support team are here to help 24/7.
            </span>
          ),
        },
        {
          question: 'What AI technology does Saights use?',
          answer: (
            <span>
              We are utilizing AI technologies from leading providers, including
              OpenAI and Anthropic. By integrating these services, we’re able to
              offer our powerful features. For more information, please take a
              look at our AI policy.
            </span>
          ),
        },
        {
          // TODO: Add a link to booking form here
          question: 'Can I get a Demo or free trial of Saights?',
          answer: (
            <span>
              We are currently in private beta. You can sign up for our waitlist
              to get early access to our platform. We will be sending out
              invites to our waitlist users soon.
            </span>
          ),
        },
      ],
    },
    {
      section: 'Support',
      qa: [
        {
          question: 'What kind of customer support does Saights offer?',
          answer: (
            <span>
              We maintain a technical support team that is available 24/7 to
              assist you with any questions or issues you may have. You can
              reach us via{' '}
              <Link className="text-brand" href="mailto:support@saights.io">
                support@saights.io
              </Link>
            </span>
          ),
        },
        {
          question: 'What data does Saights collect and how is it used?',
          answer: (
            <span>
              Saights collects survey response data to provide insights and
              analytics to our customers. We take privacy very seriously and do
              not share or sell any personal information. All data is anonymized
              and aggregated before being used for analysis. You can review our{' '}
              <Link href="/privacy" className="text-brand">
                privacy policy
              </Link>{' '}
              and{' '}
              <Link href="/ai-policy" className="text-brand">
                AI policy
              </Link>{' '}
              for more details on how we handle and protect user data.
            </span>
          ),
        },
      ],
    },
    {
      section: 'Customization',
      qa: [
        {
          question: 'Can I customize my surveys and data analaysis reports?',
          answer: (
            <span>
              Generally you are capable of customizing your surveys, we also
              offer a range of design templates and you can also individually
              customize questions. Regarding the data analysis, we offer a
              customizable export process.
            </span>
          ),
        },
      ],
    },
    {
      section: 'Integration',
      qa: [
        {
          question: 'Can I integrate Saights surveys into my digital channels?',
          answer: (
            <span>
              Saights makes it simple to share your surveys. You can generate a
              shareable URL or create a QR code, allowing you to deploy your
              surveys through various channels with ease. We&apos;re also
              developing API and SDK integrations to enable even more seamless
              survey deployment.
            </span>
          ),
        },
      ],
    },
    {
      section: 'Enterprise & Partnerships',
      qa: [
        {
          question: 'Are you searching for partners or investors?',
          answer: (
            <span>
              Yes, we are actively seeking partnerships with organizations that
              can help us grow and scale our platform. If you are interested in
              partnering with us, please reach out to us at{' '}
              <Link
                className="text-brand"
                href="mailto:partnerships@saights.io"
              >
                partnerships@saights.io
              </Link>
            </span>
          ),
        },
      ],
    },
  ],
  footer: {
    info: [
      {
        title: 'Product',
        links: [
          { href: '/#features', text: 'Features', icon: null },
          { href: '/#how-it-works', text: 'How It Works', icon: null },
          { href: '/#contact-section', text: 'Book a Demo', icon: null },
        ],
      },
    ] as {
      title: string;
      links: { href: string; text: string; icon: ReactNode }[];
    }[],
    social: {
      title: 'Stay Connected',
      links: [] as { href: string; text: string; icon: ReactNode }[],
    },
  },
};
